import { OpeningDetailResponse } from 'api-clients/monolith/models/OpeningDetailResponse';
import { useMemo } from 'react';

import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';

import { ExpandedView } from './expanded-view';
import { SummaryView } from './summary-view';

interface JobCardProps {
  opening: OpeningDetailResponse;
  onRequestRefreshBanner?: () => void;
}

export function JobCard({ opening, onRequestRefreshBanner }: JobCardProps) {
  const { getCurrencySymbol } = useCurrencySymbol();
  const { hookForm, selectedOpeningIds } = useJobPostingContext();
  const { watch, setValue } = hookForm;
  const isExpanded = watch(`jobDetails.${opening.external_id}.isExpanded`);

  const toggleExpanded = () => {
    setValue(`jobDetails.${opening.external_id}.isExpanded`, !isExpanded);

    // Close all other opening cards when one is expanded
    const otherOpeningIds = selectedOpeningIds.filter(
      id => id !== opening.external_id,
    );
    otherOpeningIds.forEach(id => {
      setValue(`jobDetails.${id}.isExpanded`, false);
    });
  };

  const payDisplay = useMemo(() => {
    const currencySymbol = getCurrencySymbol(opening.pay_currency_code);
    const payTypeSuffix = opening.pay_type ? `/${opening.pay_type}` : '';

    if (
      opening.pay_is_range &&
      opening.pay_min != null &&
      opening.pay_max != null
    ) {
      return `${currencySymbol}${opening.pay_min} - ${currencySymbol}${opening.pay_max}${payTypeSuffix}`;
    }

    if (opening.pay != null) {
      return `${currencySymbol}${opening.pay}${payTypeSuffix}`;
    }

    return null;
  }, [getCurrencySymbol, opening]);

  return isExpanded ? (
    <ExpandedView
      opening={opening}
      payDisplay={payDisplay}
      toggleExpanded={toggleExpanded}
      onRequestRefreshBanner={onRequestRefreshBanner}
    />
  ) : (
    <SummaryView
      opening={opening}
      payDisplay={payDisplay}
      toggleExpanded={toggleExpanded}
    />
  );
}
